// src/components/forms/shared/SpacerRow.tsx

// USE BORDER LIKE THIS:
// String format: valueBorder={'borderTop: 5px dotted red'}
// Object format: valueBorder={{ top: '5px dotted red', bottom: '2px solid blue' }}
// Detailed format: valueBorder={{ top: { width: '5px', style: 'dotted', color: 'red' } }}
// valueBorder={'1px solid #000000'}
// OR:
// valueBorder={{
//   top: '1px solid #000000',
//   right: '2px dotted red',
//   bottom: '1px solid #000000',
//   left: 'none'
// }}

import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import {
  FormFieldRow,
  SpacerRowLabelElement,
  SpacerRowValueElement,
} from './FormElements';

// Border configuration for a single side
interface BorderSideConfig {
  width?: string;
  style?: string;
  color?: string;
}

// Border configuration for all sides
interface BorderConfig {
  top?: string | BorderSideConfig;
  right?: string | BorderSideConfig;
  bottom?: string | BorderSideConfig;
  left?: string | BorderSideConfig;
}

interface SpacerRowProps {
  // Height configuration
  height?: string; // Default: '1rem' - applies to both label and value on desktop (side-by-side)
  labelHeightMobile?: string; // Height for label side on mobile (0px if not set) - stacked vertically
  valueHeightMobile?: string; // Height for value side on mobile (1rem if not set) - stacked vertically

  // Label styling
  labelBackgroundColor?: string;
  labelBackgroundColorMobile?: string;
  labelBorder?: BorderConfig | string; // Border on label side
  labelBorderMobile?: BorderConfig | string; // Mobile-specific label border overrides
  labelSx?: SxProps; // Custom sx props for label side

  // Value styling
  valueBackgroundColor?: string;
  valueBackgroundColorMobile?: string;
  valueBorder?: BorderConfig | string; // Border on value side
  valueBorderMobile?: BorderConfig | string; // Mobile-specific value border overrides
  valueSx?: SxProps; // Custom sx props for value side

  // Backward compatibility (deprecated in favor of granular options)
  backgroundColor?: string;
  backgroundColorMobile?: string;
  borderBottom?: boolean | string;
}

const parseBorderConfig = (
  config?: BorderConfig | string
): Record<
  'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft',
  string | undefined
> => {
  if (!config) {
    return {
      borderTop: undefined,
      borderRight: undefined,
      borderBottom: undefined,
      borderLeft: undefined,
    };
  }

  // If it's a string shorthand
  if (typeof config === 'string') {
    return {
      borderTop: config,
      borderRight: config,
      borderBottom: config,
      borderLeft: config,
    };
  }

  // If it's an object, parse each side
  const parseSide = (side?: string | BorderSideConfig): string | undefined => {
    if (!side) return undefined;
    if (typeof side === 'string') return side;

    // Build border string from width, style, color
    const parts = [];
    if (side.width) parts.push(side.width);
    if (side.style) parts.push(side.style);
    if (side.color) parts.push(side.color);
    return parts.length > 0 ? parts.join(' ') : undefined;
  };

  return {
    borderTop: parseSide(config.top),
    borderRight: parseSide(config.right),
    borderBottom: parseSide(config.bottom),
    borderLeft: parseSide(config.left),
  };
};

export const SpacerRow: React.FC<SpacerRowProps> = ({
  height = '1rem',
  labelHeightMobile,
  valueHeightMobile,
  labelBackgroundColor,
  labelBackgroundColorMobile,
  labelBorder,
  labelBorderMobile,
  labelSx,
  valueBackgroundColor,
  valueBackgroundColorMobile,
  valueBorder,
  valueBorderMobile,
  valueSx,
  // Backward compatibility
  backgroundColor,
  backgroundColorMobile,
  borderBottom,
}) => {
  // Parse border configurations
  const labelDesktopBorders = parseBorderConfig(labelBorder);
  const labelMobileBorders = parseBorderConfig(labelBorderMobile);
  const valueDesktopBorders = parseBorderConfig(valueBorder);
  const valueMobileBorders = parseBorderConfig(valueBorderMobile);

  // Handle backward compatibility for borderBottom
  const getBorderBottomStyle = (): string | undefined => {
    if (!borderBottom) return undefined;
    if (typeof borderBottom === 'string') return borderBottom;
    return '1px solid';
  };

  // Determine background colors (new granular props take precedence)
  const labelBgDesktop = labelBackgroundColor ?? backgroundColor;
  const labelBgMobile = labelBackgroundColorMobile ?? backgroundColorMobile;
  const valueBgDesktop = valueBackgroundColor;
  const valueBgMobile = valueBackgroundColorMobile;

  // Helper to get border value for a side
  const getBorderValue = (
    desktopBorder: string | undefined
  ): string | undefined => {
    return desktopBorder ?? (borderBottom ? getBorderBottomStyle() : undefined);
  };

  // Value border (desktop)
  const valueDesktopBorderTop = getBorderValue(valueDesktopBorders.borderTop);
  const valueDesktopBorderRight = getBorderValue(
    valueDesktopBorders.borderRight
  );
  const valueDesktopBorderBottom = getBorderValue(
    valueDesktopBorders.borderBottom
  );
  const valueDesktopBorderLeft = getBorderValue(valueDesktopBorders.borderLeft);

  // Value border (mobile)
  const valueMobileBorderTop =
    valueMobileBorders.borderTop ?? valueDesktopBorderTop;
  const valueMobileBorderRight =
    valueMobileBorders.borderRight ?? valueDesktopBorderRight;
  const valueMobileBorderBottom =
    valueMobileBorders.borderBottom ?? valueDesktopBorderBottom;
  const valueMobileBorderLeft =
    valueMobileBorders.borderLeft ?? valueDesktopBorderLeft;

  // Label border (desktop)
  const labelDesktopBorderTop = labelDesktopBorders.borderTop;
  const labelDesktopBorderRight = labelDesktopBorders.borderRight;
  const labelDesktopBorderBottom = labelDesktopBorders.borderBottom;
  const labelDesktopBorderLeft = labelDesktopBorders.borderLeft;

  // Label border (mobile)
  const labelMobileBorderTop =
    labelMobileBorders.borderTop ?? labelDesktopBorderTop;
  const labelMobileBorderRight =
    labelMobileBorders.borderRight ?? labelDesktopBorderRight;
  const labelMobileBorderBottom =
    labelMobileBorders.borderBottom ?? labelDesktopBorderBottom;
  const labelMobileBorderLeft =
    labelMobileBorders.borderLeft ?? labelDesktopBorderLeft;

  // Build label sx
  const labelSxStyles: any = {
    ...(labelBgDesktop ? { backgroundColor: labelBgDesktop } : {}),
    height: '100%',
    width: '100%',
    ...(labelDesktopBorderTop ? { borderTop: labelDesktopBorderTop } : {}),
    ...(labelDesktopBorderRight
      ? { borderRight: labelDesktopBorderRight }
      : {}),
    ...(labelDesktopBorderBottom
      ? { borderBottom: labelDesktopBorderBottom }
      : {}),
    ...(labelDesktopBorderLeft ? { borderLeft: labelDesktopBorderLeft } : {}),
    '@media (max-width: 600px)': {
      height: labelHeightMobile ?? '0px',
      ...((labelBgMobile ?? labelBgDesktop)
        ? { backgroundColor: labelBgMobile ?? labelBgDesktop }
        : {}),
      ...(labelMobileBorderTop ? { borderTop: labelMobileBorderTop } : {}),
      ...(labelMobileBorderRight
        ? { borderRight: labelMobileBorderRight }
        : {}),
      ...(labelMobileBorderBottom
        ? { borderBottom: labelMobileBorderBottom }
        : {}),
      ...(labelMobileBorderLeft ? { borderLeft: labelMobileBorderLeft } : {}),
    },
    ...(labelSx ? labelSx : {}),
  };

  // Build value sx
  const valueSxStyles: any = {
    ...(valueBgDesktop ? { backgroundColor: valueBgDesktop } : {}),
    height: '100%',
    width: '100%',
    ...(valueDesktopBorderTop ? { borderTop: valueDesktopBorderTop } : {}),
    ...(valueDesktopBorderRight
      ? { borderRight: valueDesktopBorderRight }
      : {}),
    ...(valueDesktopBorderBottom
      ? { borderBottom: valueDesktopBorderBottom }
      : {}),
    ...(valueDesktopBorderLeft ? { borderLeft: valueDesktopBorderLeft } : {}),
    maxWidth: '800px',
    '@media (max-width: 600px)': {
      height: valueHeightMobile ?? '1rem',
      ...((valueBgMobile ?? valueBgDesktop)
        ? { backgroundColor: valueBgMobile ?? valueBgDesktop }
        : {}),
      ...(valueMobileBorderTop ? { borderTop: valueMobileBorderTop } : {}),
      ...(valueMobileBorderRight
        ? { borderRight: valueMobileBorderRight }
        : {}),
      ...(valueMobileBorderBottom
        ? { borderBottom: valueMobileBorderBottom }
        : {}),
      ...(valueMobileBorderLeft ? { borderLeft: valueMobileBorderLeft } : {}),
    },
    ...(valueSx ? valueSx : {}),
  };

  return (
    <FormFieldRow
      sx={{
        height,
        '@media (max-width: 600px)': {
          height: 'auto',
        },
      }}
    >
      <SpacerRowLabelElement>
        <Box sx={labelSxStyles} />
      </SpacerRowLabelElement>
      <SpacerRowValueElement>
        <Box sx={valueSxStyles} />
      </SpacerRowValueElement>
    </FormFieldRow>
  );
};
